/**
 * Générateur — Il décompose un nombre entier en produit de facteurs premiers
 * (à la main, à l'aide d'un tableur ou d'un logiciel de programmation).
 * Attendu ID : 205 | Niveau : 3ème | Domaine : Divisibilité (d10, O5)
 *
 * Source doc prof (NOMBRE ET CALCULS 3e, séances A5S1-A5S2 + Créé n°5). Plan
 * de cartes auto-validé 2026-07-16 (journal TODO_EXERCISEUR_3E.md §6) :
 *
 *   « terminer »    — terminer une décomposition non aboutie (faux premiers)
 *   « batterie »    — décomposer N (bases + exposants en trous, ordre croissant)
 *   « pgcdforme »   — plus grand diviseur commun lu sur la FORME (min des exposants)
 *   « diviseurs »   — diviseurs de N = p·q·r (QCM multiple)
 *   « multiple »    — y multiple de x ? (QCM par comparaison d'exposants)
 *   « modulo »      — script pair/impair (drag 2 trous)
 *   « sortie »      — que dit le script pour N ? (QCM, parité calculée)
 *   « machine »     — machine à décomposer : exécution à la main (trous)
 *   « tableur »     — formule =MOD(A1;m)=0 (QCM)
 *
 * Convention TROUS : bases et exposants dans des trous SÉPARÉS (« 2^{{2}} »),
 * jamais d'exposant Unicode à taper. Affichage : Unicode (fmtDecomp).
 */

import {
    randomChoice, randInt, shuffle, tex,
    makeQcm, makeTexteTrou, filtrerDistracteurs,
} from '../../../utils';
import { recomposer, fusionner, decompFrag } from './divisibilite';

const ATTENDU_ID = 205;

const L_TERMINER = 'Terminer une décomposition non aboutie';
const L_BATTERIE = 'Décomposer un entier en produit de facteurs premiers';
const L_PGCDFORME = 'Lire le plus grand diviseur commun sur les décompositions';
const L_DIVISEURS = 'Trouver les diviseurs grâce à la décomposition';
const L_MULTIPLE = 'Reconnaître un multiple grâce aux exposants';
const L_MODULO = 'Compléter un script utilisant le modulo';
const L_SORTIE = 'Prévoir la sortie d\'un script pair/impair';
const L_MACHINE = 'Exécuter la machine à décomposer';
const L_TABLEUR = 'Tester une divisibilité au tableur';

const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Fragment KaTeX à trous : bases croissantes, chaque base en trou, exposant en
// trou séparé quand ≥ 2. L'exposant est protégé par la triple accolade
// (`{{2}}^{{{3}}}`) — sans le groupe, KaTeX refuse le `^` suivi du markup de
// slot et toute la phrase disparaît (FORMATS_TANOED §0.5bis).
const phraseDecomp = (dec) =>
    Object.keys(dec).map(Number).sort((a, b) => a - b)
        .map(p => (dec[p] >= 2 ? `{{${p}}}^{{{${dec[p]}}}}` : `{{${p}}}`))
        .join(' \\times ');

// ═════════ « terminer » — décomposition non aboutie ═════════

// Faux premiers avec leur décomposition (le piège diagnostic du doc).
const FAUX = [
    [25, { 5: 2 }], [9, { 3: 2 }], [49, { 7: 2 }], [27, { 3: 3 }], [121, { 11: 2 }],
    [15, { 3: 1, 5: 1 }], [21, { 3: 1, 7: 1 }], [35, { 5: 1, 7: 1 }],
    [6, { 2: 1, 3: 1 }], [10, { 2: 1, 5: 1 }], [14, { 2: 1, 7: 1 }],
    [33, { 3: 1, 11: 1 }], [55, { 5: 1, 11: 1 }],
];

const genTerminerTrous = () => {
    const [fAff, fDec] = randomChoice(FAUX);
    // 1-2 facteurs visibles déjà premiers (chevauchement possible → fusion).
    const nb = randInt(1, 2);
    const visibles = [];
    let extras = {};
    for (let i = 0; i < nb; i++) {
        const p = randomChoice([2, 3, 5, 7, 11, 13]);
        const e = randInt(1, 2);
        visibles.push(e >= 2 ? `${p}^{${e}}` : String(p));
        extras = fusionner(extras, { [p]: e });
    }
    const total = fusionner(extras, fDec);
    const expr = shuffle([...visibles, String(fAff)]).join(' \\times ');
    const phrase = `$${expr} = ${phraseDecomp(total)}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Le produit ${tex(expr)} n'est pas une décomposition aboutie : certains facteurs ne sont pas premiers.</p>`
        + '<p><b>Termine la décomposition en facteurs premiers</b>, bases en ordre croissant.</p>',
        { ...opts(L_TERMINER), mode: 'saisie' });
    payload._meta.params = { expr, total };
    return payload;
};

// ═════════ « batterie » — décomposer N ═════════

const genBatterieTrous = () => {
    let dec; let N;
    for (let essai = 0; essai < 60; essai++) {
        dec = {};
        const a = randInt(1, 2); if (a) dec[2] = a;
        const b = randInt(0, 3); if (b) dec[3] = b;
        const c = randInt(0, 1); if (c) dec[5] = c;
        if (randInt(0, 1)) dec[randomChoice([7, 11, 13, 17, 19, 23, 29, 31, 37])] = 1;
        N = recomposer(dec);
        const nbPremiers = Object.keys(dec).length;
        if (nbPremiers >= 2 && N >= 60 && N <= 2500) break;
        dec = null;
    }
    if (!dec) { dec = { 2: 1, 3: 2, 17: 1 }; N = 306; } // repli (doc Ex.2)
    const phrase = `$${N} = ${phraseDecomp(dec)}$`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Décompose ${N} en produit de facteurs premiers</b>, bases en ordre croissant.</p>`,
        { ...opts(L_BATTERIE), mode: 'saisie' });
    payload._meta.params = { N, dec };
    return payload;
};

// ═════════ « pgcdforme » — PGCD lu sur les décompositions ═════════

const genPgcdFormeTrous = () => {
    let p; let q; let i1; let i2; let j1; let j2; let i; let j; let val;
    for (let essai = 0; essai < 60; essai++) {
        [p, q] = shuffle([3, 5, 7]).slice(0, 2).sort((x, y) => x - y);
        i1 = randInt(2, 4); i2 = randInt(2, 5); j1 = randInt(1, 2); j2 = randInt(1, 3);
        i = Math.min(i1, i2); j = Math.min(j1, j2);
        val = p ** i * q ** j;
        if (i1 !== i2 && val <= 2500 && i >= 2) break;
        val = null;
    }
    if (!val) { p = 3; q = 7; i1 = 4; i2 = 5; j1 = 1; j2 = 2; i = 4; j = 1; val = 567; } // repli (doc Ex.3)
    const extra = randInt(0, 1) ? '2 \\times ' : '';
    const affA = decompFrag({ [p]: i1, [q]: j1 });
    const affB = extra + decompFrag({ [p]: i2, [q]: j2 });
    const phrase = j >= 2
        ? `$D = {{${p}}}^{{{${i}}}} \\times {{${q}}}^{{{${j}}}} = {{${val}}}$`
        : `$D = {{${p}}}^{{{${i}}}} \\times {{${q}}} = {{${val}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${tex(`a = ${affA}`)} et ${tex(`b = ${affB}`)}.</p>`
        // « le plus petit des deux exposants » EST la règle à trouver.
        + '<p><b>Sans calculer a ni b, donne le plus grand diviseur commun D de a et b</b>, '
        + 'puis sa valeur.</p>',
        { ...opts(L_PGCDFORME), mode: 'saisie' });
    payload._meta.params = { p, q, i1, i2, j1, j2, val };
    return payload;
};

// ═════════ « diviseurs » — QCM multiple sur N = p·q·r ═════════

const TRIPLES = [[3, 5, 7], [3, 5, 11], [3, 7, 11], [5, 7, 11], [2, 3, 7], [2, 5, 7], [7, 11, 13], [2, 3, 11], [2, 7, 11], [3, 5, 13]];

const genDiviseursQcm = () => {
    const [p, q, r] = randomChoice(TRIPLES);
    const N = p * q * r;
    const propres = [p, q, r, p * q, p * r, q * r];
    const corrects = shuffle(propres).slice(0, 4).map(String);
    const faux = [String(N)];                              // « autre que lui-même »
    faux.push(String(p * p));                              // p² ne divise pas (sans carré)
    let s = p + q;                                          // somme : non-diviseur vérifié
    while (N % s === 0 || propres.includes(s) || s === N || faux.includes(String(s))) s += 1;
    faux.push(String(s));
    const choix = shuffle([...corrects, ...faux]);
    const payload = makeQcm(choix, corrects,
        `<p>On a décomposé ${tex(`N = ${N} = ${p} \\times ${q} \\times ${r}`)}.</p>`
        + '<p><b>Sélectionne les quatre nombres qui sont des diviseurs de N</b> '
        + 'autres que 1 et N lui-même.</p>',
        opts(L_DIVISEURS), true);
    payload._meta.params = { p, q, r, corrects };
    return payload;
};

// ═════════ « multiple » — y multiple de x ? (exposants) ═════════

const genMultipleQcm = () => {
    const [p, q] = shuffle([3, 5, 7]).slice(0, 2).sort((x, y) => x - y);
    const a = randInt(2, 4); const b = randInt(1, 3);
    const cas = randomChoice(['oui', 'p_trop_petit', 'q_trop_petit']);
    let c; let d;
    if (cas === 'oui') { c = a + randInt(0, 2); d = b + randInt(0, 2); }
    else if (cas === 'p_trop_petit') { c = a - randInt(1, Math.min(2, a)); d = b + randInt(0, 2); }
    else { c = a + randInt(0, 2); d = b - randInt(1, Math.min(2, b)); }
    const affX = decompFrag({ [p]: a, [q]: b });
    const decY = {};                                   // exposant 0 → facteur ABSENT de l'affichage
    if (c > 0) decY[p] = c;
    if (d > 0) decY[q] = d;
    const affY = (randInt(0, 1) ? '2 \\times ' : '') + decompFrag(decY);
    const choixOui = 'Oui : chaque exposant dans x est inférieur ou égal à l\'exposant correspondant dans y.';
    const choixP = `Non : l'exposant de ${p} est plus grand dans x que dans y.`;
    const choixQ = `Non : l'exposant de ${q} est plus grand dans x que dans y.`;
    const correct = cas === 'oui' ? choixOui : (cas === 'p_trop_petit' ? choixP : choixQ);
    const payload = makeQcm([choixOui, choixP, choixQ], correct,
        `<p>On pose ${tex(`x = ${affX}`)} et ${tex(`y = ${affY}`)}.</p>`
        + '<p><b>Sans calculer x ni y : y est-il un multiple de x ?</b></p>',
        opts(L_MULTIPLE));
    payload._meta.params = { p, q, a, b, c, d, cas };
    return payload;
};

// ═════════ « modulo » — script pair/impair (drag) ═════════

const PRENOMS = ['Lina', 'Adam', 'Sofia', 'Noé', 'Jade', 'Rayan', 'Maya', 'Tom', 'Inès', 'Loïc', 'Zoé', 'Malo'];

const genModuloDrag = () => {
    const prenom = randomChoice(PRENOMS);
    const surZero = randInt(0, 1) === 1;   // condition « modulo 2 = 0 » ou « = 1 »
    const alors = surZero ? 'pair' : 'impair';
    const sinon = surZero ? 'impair' : 'pair';
    const phrase = `Script de ${prenom} : demander un nombre entier, puis SI (réponse modulo 2 = ${surZero ? 0 : 1}) `
        + `ALORS dire « … est un nombre {{${alors}}} » SINON dire « … est un nombre {{${sinon}}} ».`;
    // Pas de « entier » (formerait une phrase vraie) : le script teste la PARITÉ.
    const distracteurs = filtrerDistracteurs(phrase, ['positif', 'négatif']);
    const payload = makeTexteTrou(phrase,
        '<p>Dans un logiciel de programmation, « a modulo b » donne le reste de la division euclidienne de a par b.</p>'
        + '<p><b>Complète le script avec les bons mots.</b></p>',
        { ...opts(L_MODULO), mode: 'drag', distracteurs });
    payload._meta.params = { prenom, surZero };
    return payload;
};

// ═════════ « sortie » — que dit le script pour N ? (QCM) ═════════

const genSortieQcm = () => {
    const N = randInt(11, 199);
    const correct = `${N} est un nombre ${N % 2 === 0 ? 'pair' : 'impair'}.`;
    const faux1 = `${N} est un nombre ${N % 2 === 0 ? 'impair' : 'pair'}.`;
    const faux2 = 'Le script affiche le quotient de la division par 2.';
    const payload = makeQcm([correct, faux1, faux2], correct,
        '<p>Un script demande un nombre entier, puis : SI (réponse modulo 2 = 0) ALORS dire '
        + '« … est un nombre pair » SINON dire « … est un nombre impair ».</p>'
        + `<p><b>Que dit le script si on lui donne ${N} ?</b></p>`,
        opts(L_SORTIE));
    payload._meta.params = { N };
    return payload;
};

// ═════════ « machine » — exécuter la machine à décomposer ═════════

const genMachineTrous = () => {
    let N; let facteurs;
    for (let essai = 0; essai < 60; essai++) {
        const dec = {};
        dec[2] = randInt(0, 3); dec[3] = randInt(0, 2); dec[5] = randInt(0, 1);
        dec[7] = randInt(0, 1); dec[11] = randInt(0, 1);
        facteurs = [];
        for (const p of [2, 3, 5, 7, 11]) for (let i = 0; i < (dec[p] || 0); i++) facteurs.push(p);
        N = facteurs.reduce((x, y) => x * y, 1);
        if (facteurs.length >= 3 && facteurs.length <= 4 && N <= 500 && N >= 24) break;
        facteurs = null;
    }
    if (!facteurs) { facteurs = [2, 2, 3, 5]; N = 60; } // repli (doc Créé n°5)
    const trousFacteurs = facteurs.map(f => `{{${f}}}`).join(' ; ');
    const phrase = `Bloc manquant : « ajouter {{1}} à d ». Pour N = ${N}, le script affiche : ${trousFacteurs}.`;
    const payload = makeTexteTrou(phrase,
        // Pseudo-code en <code> : monospace à l'affichage, et le lint de style
        // n'y cherche ni parenthèse explicative ni fraction à typographier.
        '<p>Machine à décomposer :</p>'
        + '<p><code>mettre d à 2, puis répéter jusqu\'à ce que N = 1 : '
        + 'SI (N modulo d = 0) ALORS [dire d ; mettre N à N/d] SINON [ajouter … à d]</code></p>'
        + `<p><b>Complète le bloc manquant, puis exécute le script à la main pour N = ${N}.</b> `
        + 'Donne les facteurs dans l\'ordre où le script les affiche.</p>',
        { ...opts(L_MACHINE), mode: 'saisie' });
    payload._meta.params = { N, facteurs };
    return payload;
};

// ═════════ « tableur » — formule =MOD(A1;m)=0 (QCM) ═════════

const genTableurQcm = () => {
    const m = randomChoice([3, 4, 5, 6, 7, 9, 11, 13]);
    const cellule = randomChoice(['A1', 'A2', 'B1', 'B3', 'C2']);
    const correct = `=MOD(${cellule};${m})=0`;
    const choix = [correct, `=MOD(${m};${cellule})=0`, `=${cellule}/${m}`, `=MOD(${cellule};${m})=1`];
    const payload = makeQcm(choix, correct,
        '<p>Au tableur, MOD(a;b) donne le reste de la division euclidienne de a par b.</p>'
        + `<p><b>Quelle formule teste si le nombre écrit en ${cellule} est divisible par ${m} ?</b></p>`,
        opts(L_TABLEUR));
    payload._meta.params = { m, cellule };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    terminer_texte_trou_saisie: genTerminerTrous,
    batterie_texte_trou_saisie: genBatterieTrous,
    pgcdforme_texte_trou_saisie: genPgcdFormeTrous,
    diviseurs_qcm_multiple: genDiviseursQcm,
    multiple_qcm_unique: genMultipleQcm,
    modulo_texte_trou_drag: genModuloDrag,
    sortie_qcm_unique: genSortieQcm,
    machine_texte_trou_saisie: genMachineTrous,
    tableur_qcm_unique: genTableurQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte_trou_saisie: 'Saisie trous',
    texte_trou_drag: 'Drag',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'terminer', label: L_TERMINER, formatsList: buildFormats('terminer') },
    { id: 'batterie', label: L_BATTERIE, formatsList: buildFormats('batterie') },
    { id: 'pgcdforme', label: L_PGCDFORME, formatsList: buildFormats('pgcdforme') },
    { id: 'diviseurs', label: L_DIVISEURS, formatsList: buildFormats('diviseurs') },
    { id: 'multiple', label: L_MULTIPLE, formatsList: buildFormats('multiple') },
    { id: 'modulo', label: L_MODULO, formatsList: buildFormats('modulo') },
    { id: 'sortie', label: L_SORTIE, formatsList: buildFormats('sortie') },
    { id: 'machine', label: L_MACHINE, formatsList: buildFormats('machine') },
    { id: 'tableur', label: L_TABLEUR, formatsList: buildFormats('tableur') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
